import axios from 'axios';
import { BASE_URL, BASE_NODE_URL, REPORTS_BASE_URL, ELASTIC_SEARCH_BASE_URL } from '../Constants/Config';
import { getAuthDetails } from '../Common/cookiesHelper';
import { clearSession, saveLogoutReason } from '../Common/sessionLogout';
import { clearAllChatbotConversations } from '../utils/chatbotConversationStorage';

/**
 * Every first-party API call (PHP, Node, reports, elastic) carries the signed-in session
 * when one exists. Call sites that already send a field keep their value.
 *
 * Header:
 *   Authorization: Bearer <token>  (also replaces a blank "Bearer undefined")
 *
 * Body (or query string, when the request has no body — GET/HEAD):
 *   login_user_type, token_id, login_type, session_id,
 *   login_user_email, login_merchant_id
 *
 * merchant_id is never touched: a call that deliberately targets another store
 * keeps that value and is authorised by login_merchant_id instead.
 * Arrays, Blob/File, and other non-object bodies are left unchanged.
 * A request with no session token is sent exactly as the caller built it.
 */
const SESSION_BODY_KEYS = [
  'login_user_type',
  'token_id',
  'login_type',
  'session_id',
  'login_user_email',
  'login_merchant_id',
];

function isApiUrl(url, base) {
  return typeof url === 'string' && !!base && url.startsWith(base);
}

// The bare elastic root is CheckElasticConnection's health probe, not an API route; it stays a plain GET.
function isElasticApiUrl(url) {
  return isApiUrl(url, ELASTIC_SEARCH_BASE_URL) && url !== ELASTIC_SEARCH_BASE_URL;
}

function isFirstPartyUrl(url) {
  return (
    isApiUrl(url, BASE_URL) ||
    isApiUrl(url, BASE_NODE_URL) ||
    isApiUrl(url, REPORTS_BASE_URL) ||
    isElasticApiUrl(url)
  );
}

function isBlankBearer(value) {
  if (!value) return true;
  const m = /^Bearer\s*(.*)$/i.exec(String(value).trim());
  if (!m) return false;
  const token = m[1].trim();
  return token === '' || token.toLowerCase() === 'undefined' || token.toLowerCase() === 'null';
}

// A plain {} body only: arrays, Blob/File, typed arrays, FormData and URLSearchParams are never spread.
function isPlainObjectBody(data) {
  return Object.prototype.toString.call(data) === '[object Object]';
}

function isMissing(value) {
  return value === undefined || value === null || value === '';
}

function presentPatch(patch) {
  const out = {};
  for (const key of SESSION_BODY_KEYS) {
    if (!isMissing(patch[key])) out[key] = patch[key];
  }
  return out;
}

/**
 * Fill missing body fields without overwriting ones the caller already set. A plain object is copied
 * (callers sometimes reuse it); FormData/URLSearchParams are appended to in place.
 */
function fillMissingBodyFields(config, patch) {
  const keys = Object.keys(patch);
  if (!keys.length) return false;

  if (isPlainObjectBody(config.data)) {
    const toAdd = {};
    for (const key of keys) {
      if (isMissing(config.data[key])) toAdd[key] = patch[key];
    }
    if (Object.keys(toAdd).length) {
      config.data = { ...config.data, ...toAdd };
    }
    return true;
  }
  if (config.data instanceof FormData) {
    for (const key of keys) {
      if (!config.data.has(key)) config.data.append(key, patch[key]);
    }
    return true;
  }
  if (config.data instanceof URLSearchParams) {
    for (const key of keys) {
      if (!config.data.has(key)) config.data.append(key, String(patch[key]));
    }
    return true;
  }
  return false;
}

function fillMissingParams(config, patch) {
  const keys = Object.keys(patch);
  if (!keys.length) return;

  if (config.params instanceof URLSearchParams) {
    for (const key of keys) {
      if (!config.params.has(key)) config.params.append(key, String(patch[key]));
    }
    return;
  }
  if (config.params == null) {
    config.params = {};
  }
  if (!isPlainObjectBody(config.params)) return;
  const toAdd = {};
  for (const key of keys) {
    if (isMissing(config.params[key])) toAdd[key] = patch[key];
  }
  if (Object.keys(toAdd).length) {
    config.params = { ...config.params, ...toAdd };
  }
}

function sessionPatch(userTypeData) {
  return presentPatch({
    login_user_type: userTypeData?.login_user_type,
    token_id: userTypeData?.token_id,
    login_type: userTypeData?.login_type,
    session_id: userTypeData?.session_id,
    login_user_email: userTypeData?.login_user_email,
    login_merchant_id: userTypeData?.login_merchant_id,
  });
}

axios.interceptors.request.use((config) => {
  try {
    const url = config.url;
    if (!isFirstPartyUrl(url)) {
      return config;
    }

    const { userTypeData } = getAuthDetails();
    const token = userTypeData?.token;
    if (!token) {
      return config; // no session yet — nothing to inject
    }

    const existingAuth = config.headers && (config.headers.Authorization || config.headers.authorization);
    if (!existingAuth || isBlankBearer(existingAuth)) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    const patch = sessionPatch(userTypeData);
    const method = String(config.method || 'get').toLowerCase();
    if ((config.data == null || config.data === '') && (method === 'post' || method === 'put' || method === 'patch')) {
      config.data = {};
    }
    const filledBody = fillMissingBodyFields(config, patch);
    // GET/HEAD have no body the server will read; the same keys go on the query string.
    if (!filledBody && (method === 'get' || method === 'head' || method === '')) {
      fillMissingParams(config, patch);
    }
  } catch (e) {
    // Never let interceptor logic itself block a request from going out.
  }
  return config;
});

/**
 * A 401 from a first-party API while signed in means the session is no longer
 * valid: log out at once (same cleanup as the Logout button) and land on the
 * login page with the reason. Runs once per page load, so a burst of parallel
 * 401s triggers a single logout. Requests made without a session (the login
 * form itself) are left to their callers. The error is still rejected so call
 * sites finish their own cleanup.
 */
const DEFAULT_LOGOUT_REASON = 'Your session has expired. Please log in again.';
let sessionExpiryHandled = false;

function logoutReasonFrom(error) {
  const message = error?.response?.data?.message;
  if (typeof message !== 'string') return DEFAULT_LOGOUT_REASON;
  const trimmed = message.trim();
  if (!trimmed || /^unauthori[sz]ed\.?$/i.test(trimmed)) return DEFAULT_LOGOUT_REASON;
  return `You were logged out: ${trimmed}`;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const isSessionExpired =
        error?.response?.status === 401 &&
        isFirstPartyUrl(error?.config?.url) &&
        Boolean(getAuthDetails()?.userTypeData?.token);

      if (isSessionExpired && !sessionExpiryHandled) {
        sessionExpiryHandled = true;
        saveLogoutReason(logoutReasonFrom(error));
        clearAllChatbotConversations();
        clearSession();
        window.location.replace(`${process.env.PUBLIC_URL || ''}/login`);
      }
    } catch (e) {
      // Never let the logout path mask the original API error.
    }
    return Promise.reject(error);
  }
);
